/**
 * Journey Store
 *
 * Tracks current journey state by observing the router and provides navigation methods.
 * Provides computed helpers, zone configurations, and handles next/previous navigation.
 * Router is still source of truth - store syncs via guard and provides navigation helpers.
 *
 * Separate from CheckoutStore which handles booking domain data.
 */

import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { signalStore, withState, withComputed, withMethods, patchState } from '@ngrx/signals';
import { withSyncToWebStorage } from '@shared/stores/store-features';
import { BookingTypeName } from '@shared/models/enums';
import { SearchQueryParams } from '@app/features/associations/models/search-response';

import {
  CheckoutJourney,
  JourneyPageConfig,
  JourneyZone,
  CheckoutStep,
  JourneyActionOverride,
} from './models/checkout-journey.types';
import { getJourneyConfig, getStepIndicatorConfig } from './config/checkout-journeys.config';
import { getAvailabilityTypeZoneConfig } from './config/availability-type.config';
import { combineAvailabilityAndJourneyConfigs, applyPageOverrides } from './config/zone-config.merger';
import { AvailabilityType } from '../booking';
import { CheckoutStore } from './checkout.store';

interface JourneyState {
  currentJourney: BookingTypeName;
  currentPageIndex: number;
  availabilityType: AvailabilityType | null; // set from basket (availabilityType guard)
  lastSearchParams: SearchQueryParams | null;
}

export const JourneyStore = signalStore(
  { providedIn: 'root' },

  withState<JourneyState>({
    currentJourney: BookingTypeName.UNION, // default journey - can be set dynamically based on booking type
    currentPageIndex: 0,
    availabilityType: null,
    lastSearchParams: null,
  }),

  withSyncToWebStorage({
    key: 'abook-journey-store',
    type: 'session',
    restoreOnInit: true,
    saveStateChangesAfterMs: 300,
  }),

  withComputed((state) => ({
    // Core journey config
    journeyConfig: computed((): CheckoutJourney => {
      return getJourneyConfig(state.currentJourney());
    }),

    // Current page config
    currentPage: computed((): JourneyPageConfig | undefined => {
      const config = getJourneyConfig(state.currentJourney());
      if (!config) {
        console.warn(`[Journey Store] Missing journey configuration for journey: ${state.currentJourney()}`);
        return undefined;
      }
      return config.page[state.currentPageIndex()];
    }),

    // Navigation computed values
    canNavigatePrevious: computed(() => state.currentPageIndex() > 0),

    canNavigateNext: computed((): boolean => {
      const config = getJourneyConfig(state.currentJourney());
      if (!config) {
        console.warn(`[Journey Store] Missing journey configuration for journey: ${state.currentJourney()}`);
        return false;
      }
      return state.currentPageIndex() < config.page.length - 1;
    }),

    isLastPage: computed((): boolean => {
      const config = getJourneyConfig(state.currentJourney());
      if (!config) {
        console.warn(`[Journey Store] Missing journey configuration for journey: ${state.currentJourney()}`);
        return false;
      }
      return state.currentPageIndex() === config.page.length - 1;
    }),

    // Step indicator config
    stepIndicatorSteps: computed((): CheckoutStep[] => {
      return getStepIndicatorConfig(state.currentJourney());
    }),
  })),

  withMethods((store) => {
    const router = inject(Router);
    const checkoutStore = inject(CheckoutStore);

    return {
      /**
       * Initialize or switch journey type
       * Resets to first page of the new journey
       */
      setJourney(bookingType: BookingTypeName): void {
        checkoutStore.setBookingType(bookingType); // same as journey type
        patchState(store, { currentJourney: bookingType, currentPageIndex: 0 });
      },

      /**
       * Set availability type for the current journey
       * Logic - A journey may only have one availability type. it is decided by the timeslot(s) the user adds to the basket.
       * This is important for validation and conditional rendering across all pages of the journey, as different availability types may have different rules and UI requirements.
       * journey can check this value to adjust their behavior accordingly (e.g. show specific messages, enable/disable certain actions, etc.). This method ensures that the journey store is aware of the availability type context of the booking process without directly coupling it to the basket logic.
       * This method allows us to set that availability type on the journey store so it can be used for validation and conditional rendering across all pages of the journey.
       * @param availabilityType
       */
      setJourneyAvailabilityType(availabilityType: AvailabilityType): void {
        patchState(store, { availabilityType });
      },

      /**
       * Store the last search parameters for "back to search" navigation
       */
      setLastSearchParams(params: SearchQueryParams): void {
        patchState(store, { lastSearchParams: params });
      },

      /**
       * Sync store to current route (called by guard)
       * This is the ONLY method that mutates page state
       * Router is source of truth - store observes router via guard
       */
      setCurrentPageByRoute(route: string): void {
        const config = store.journeyConfig();
        const pageIndex = config.page.findIndex((p) => p.pageRoute === route);

        if (pageIndex !== -1) {
          patchState(store, { currentPageIndex: pageIndex });
        }
      },

      /**
       * Get next page route (computed helper - does NOT mutate state)
       * Component uses this to know where to navigate via router
       * Returns null if at last page
       */
      getNextPageRoute(): string | null {
        const config = store.journeyConfig();
        const nextIndex = store.currentPageIndex() + 1;
        return nextIndex < config.page.length ? config.page[nextIndex].pageRoute : null;
      },

      /**
       * Get previous page route (computed helper - does NOT mutate state)
       * Component uses this to know where to navigate via router
       * Returns null if at first page
       */
      getPreviousPageRoute(): string | null {
        const config = store.journeyConfig();
        const prevIndex = store.currentPageIndex() - 1;
        return prevIndex >= 0 ? config.page[prevIndex].pageRoute : null;
      },

      /**
       * Validate current page using config rules
       * Returns true if page can be activated, false otherwise
       */
      validateCurrentPage(): boolean {
        const page = store.currentPage();
        if (!page?.canActivate) return true;
        return page.canActivate();
      },

      /**
       * Get zone config for current page
       * Provides merged config from availability-type, journey, and page sources
       *
       * Scope-Based Three-Layer Merge Strategy:
       * 1. AvailabilityType (Cross-Journey Defaults - broadest scope)
       * 2. Journey (Journey-Scoped Overrides - middle scope)
       * 3. Page (Page-Scoped Overrides - narrowest scope)
       *
       * Merge Principle: Narrower scope overrides broader scope
       * - Journey overrides AvailabilityType (journey-specific > cross-journey)
       * - Page overrides both (page-specific > journey-wide > cross-journey)
       *
       * For 'actions' zone: All 3 layers supported (merge by matching 'id')
       * For 'summaryContent' zone: Only 2 layers supported (AvailabilityType + Journey)
       *
       * See SCOPE-BASED-MERGE-STRATEGY.md for detailed explanation
       *
       * @param zone - The zone to get config for
       * @param pageOverrides - Optional page-level overrides (narrowest scope, actions zone only)
       * @returns Merged zone configuration
       */
      getZoneConfig<T>(zone: JourneyZone, pageOverrides?: JourneyActionOverride[]): T | undefined {
        const page = store.currentPage();
        const availabilityType = store.availabilityType();
        const pageRoute = page?.pageRoute;

        if (!pageRoute) {
          return undefined;
        }

        // Layer 1 & 2: Combine availability-type and journey configs
        const baseConfig = combineAvailabilityAndJourneyConfigs<T>(
          zone,
          getAvailabilityTypeZoneConfig<T>(availabilityType, pageRoute, zone),
          page?.zoneConfig?.[zone] as T | undefined,
        );

        // Layer 3: Apply page overrides (actions zone only)
        return applyPageOverrides(zone, baseConfig, pageOverrides);
      },

      /**
       * Navigate to next page
       * Performs actual router navigation to next page in journey
       * Returns true if navigation was initiated, false if at last page
       */
      async navigateNext(): Promise<boolean> {
        const nextRoute = this.getNextPageRoute();
        if (nextRoute) {
          await router.navigate(['/checkout', nextRoute]);
          return true;
        }
        return false;
      },

      /**
       * Navigate to previous page
       * Performs actual router navigation to previous page in journey
       * Returns true if navigation was initiated, false if at first page
       */
      async navigatePrevious(): Promise<boolean> {
        const prevRoute = this.getPreviousPageRoute();
        if (prevRoute) {
          await router.navigate(['/checkout', prevRoute]);
          return true;
        }
        return false;
      },
    };
  }),
);
